import React, { useState, useEffect } from 'react';
import { CalendarMonth as CalendarIcon, Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { translate } from '../../locales';
import { getAllAppointments, Appointment } from '../../services/appointmentService';
import './style.css';

const AppointmentsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = (key: any) => translate(key, language as 'en' | 'ua');

  const [activeTab, setActiveTab] = useState<'today' | 'calendar' | 'history'>('today');
  const [completedAppointments, setCompletedAppointments] = useState<Appointment[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        console.log("Starting to fetch appointments...");
        const data = await getAllAppointments();
        console.log('All appointments received:', data);
        console.log('Type of data:', typeof data);
        console.log('Is data null?', data === null);
        console.log('Is data undefined?', data === undefined);
        console.log('Data length:', data?.length);
        
        // For completed and upcoming appointments, filter by date
        const now = new Date();
        const completed = data?.filter(apt => 
          new Date(apt.appointment_date) < now
        ) || [];
        const upcoming = data?.filter(apt => 
          new Date(apt.appointment_date) >= now
        ) || [];
        
        console.log('Completed appointments:', completed);
        console.log('Upcoming appointments:', upcoming);
        
        setCompletedAppointments(completed);
        setUpcomingAppointments(upcoming);
      } catch (err) {
        console.error('Error fetching appointments:', err);
        setError(err instanceof Error ? err.message : 'Failed to load appointments');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  return (
    <div className="appointments-page">
      <div className="appointments-header">
        <h1 className="page-title">
          <i className="bi bi-calendar-check"></i>
          {t('pages.appointments.title')}
        </h1>
        <p className="page-description">
          {t('pages.appointments.description')}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="appointments-tabs-navigation">
        <button
          className={`appointments-tab-button ${activeTab === 'today' ? 'active' : ''}`}
          onClick={() => setActiveTab('today')}
        >
          <i className="bi bi-calendar-day"></i>
          <span className="appointments-tab-text">
            {language === 'ua' ? 'Сьогодні' : 'Today'}
          </span>
        </button>
        <button
          className={`appointments-tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          <i className="bi bi-calendar"></i>
          <span className="appointments-tab-text">
            {language === 'ua' ? 'Календар' : 'Calendar'}
          </span>
        </button>
        <button
          className={`appointments-tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <i className="bi bi-clock-history"></i>
          <span className="appointments-tab-text">
            {language === 'ua' ? 'Пройдені записи' : 'Completed'}
          </span>
        </button>
      </div>

      <div className="appointments-content">
        {loading && <p>{language === 'ua' ? 'Завантаження...' : 'Loading...'}</p>}
        {error && <p className="error">{error}</p>}
        {!loading && !error && (
          <>
            {activeTab === 'today' && (
              <div className="tab-content">
                <h2>{language === 'ua' ? 'Майбутні записи' : 'Upcoming Appointments'}</h2>
                {upcomingAppointments.length === 0 ? (
                  <div className="empty-state">
                    <i className="bi bi-calendar-plus" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <h3>{language === 'ua' ? 'Немає майбутніх записів' : 'No upcoming appointments'}</h3>
                    <p>{language === 'ua' ? 'Майбутні записи будуть відображатися тут' : 'Upcoming appointments will appear here'}</p>
                  </div>
                ) : (
                  <div className="appointments-list">
                    {upcomingAppointments.map((appointment: Appointment) => {
                      const formatDate = (dateString: string) => {
                        const date = new Date(dateString);
                        return date.toLocaleString(language === 'ua' ? 'uk-UA' : 'en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      };

                      const getStatusLabel = (status: string) => {
                        const labels: Record<string, string> = {
                          'scheduled': language === 'ua' ? 'Заплановано' : 'Scheduled',
                          'confirmed': language === 'ua' ? 'Підтверджено' : 'Confirmed',
                          'in_progress': language === 'ua' ? 'В роботі' : 'In Progress',
                          'completed': language === 'ua' ? 'Завершено' : 'Completed',
                          'cancelled': language === 'ua' ? 'Скасовано' : 'Cancelled',
                          'no_show': language === 'ua' ? 'Не з\'явився' : 'No Show'
                        };
                        return labels[status] || status;
                      };

                      return (
                        <div key={appointment.id} className={`appointment-item status-${appointment.status}`}>
                          <div className="appointment-info">
                            <div>
                              <div className="appointment-header">
                                <h3 className="appointment-title">
                                  <CalendarIcon className="appointment-icon" />
                                  {language === 'ua' ? 'Запис' : 'Appointment'} #{appointment.id.slice(-8)}
                                </h3>
                                <span className={`appointment-status status-${appointment.status}`}>
                                  {getStatusLabel(appointment.status)}
                                </span>
                              </div>
                              <div className="appointment-details">
                                <div className="appointment-detail">
                                  <span className="appointment-detail-label">
                                    {language === 'ua' ? 'Дата' : 'Date'}
                                  </span>
                                  <span className="appointment-detail-value">
                                    {formatDate(appointment.appointment_date)}
                                  </span>
                                </div>
                                {appointment.client && (
                                  <>
                                    <div className="appointment-detail">
                                      <span className="appointment-detail-label">
                                        {language === 'ua' ? 'Клієнт' : 'Client'}
                                      </span>
                                      <span className="appointment-detail-value">
                                        {appointment.client.name}
                                      </span>
                                    </div>
                                    <div className="appointment-detail">
                                      <span className="appointment-detail-label">
                                        {language === 'ua' ? 'Телефон' : 'Phone'}
                                      </span>
                                      <a
                                        href={`tel:${appointment.client.phone.replace('+38', '')}`}
                                        className="appointment-detail-value phone"
                                      >
                                        <PhoneIcon className="contact-icon" />
                                        {appointment.client.phone}
                                      </a>
                                    </div>
                                    <div className="appointment-detail">
                                      <span className="appointment-detail-label">
                                        {language === 'ua' ? 'Email' : 'Email'}
                                      </span>
                                      <a
                                        href={`mailto:${appointment.client.email}`}
                                        className="appointment-detail-value email"
                                      >
                                        <EmailIcon className="contact-icon" />
                                        {appointment.client.email}
                                      </a>
                                    </div>
                                  </>
                                )}
                              </div>
                              {appointment.notes && (
                                <div className="appointment-notes">
                                  <div className="appointment-notes-label">
                                    {language === 'ua' ? 'Примітки' : 'Notes'}
                                  </div>
                                  <div className="appointment-notes-value">
                                    {appointment.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'calendar' && (
          <div className="tab-content">
            <h2>{language === 'ua' ? 'Календар записів' : 'Appointments Calendar'}</h2>
            <p>{language === 'ua' ? 'Тут буде календар з усіма записами' : 'Calendar with all appointments will be here'}</p>
            {/* TODO: Implement calendar view */}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="tab-content">
            <h2>{language === 'ua' ? 'Пройдені записи' : 'Completed Appointments'}</h2>
            {loading && <p>{language === 'ua' ? 'Завантаження...' : 'Loading...'}</p>}
            {error && <p className="error">{error}</p>}
            {!loading && !error && (
              <>
                {completedAppointments.length === 0 ? (
                  <div className="empty-state">
                    <i className="bi bi-calendar-check" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <h3>{language === 'ua' ? 'Немає пройдених записів' : 'No completed appointments'}</h3>
                    <p>{language === 'ua' ? 'Пройдені записи будуть відображатися тут' : 'Completed appointments will appear here'}</p>
                  </div>
                ) : (
                  <div className="appointments-list">
                    {completedAppointments.map((appointment) => {
                      const formatDate = (dateString: string) => {
                        const date = new Date(dateString);
                        return date.toLocaleString(language === 'ua' ? 'uk-UA' : 'en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      };

                      const getStatusLabel = (status: string) => {
                        const labels: Record<string, string> = {
                          'completed': language === 'ua' ? 'Завершено' : 'Completed',
                          'cancelled': language === 'ua' ? 'Скасовано' : 'Cancelled',
                          'no_show': language === 'ua' ? 'Не з\'явився' : 'No Show'
                        };
                        return labels[status] || status;
                      };

                      return (
                        <div key={appointment.id} className={`appointment-item status-${appointment.status} completed`}>
                          <div className="appointment-info">
                            <div>
                              <div className="appointment-header">
                                <h3 className="appointment-title">
                                  <CalendarIcon className="appointment-icon" />
                                  {language === 'ua' ? 'Запис' : 'Appointment'} #{appointment.id.slice(-8)}
                                </h3>
                                <span className={`appointment-status status-${appointment.status}`}>
                                  {getStatusLabel(appointment.status)}
                                </span>
                              </div>
                              <div className="appointment-details">
                                <div className="appointment-detail">
                                  <span className="appointment-detail-label">
                                    {language === 'ua' ? 'Дата' : 'Date'}
                                  </span>
                                  <span className="appointment-detail-value">
                                    {formatDate(appointment.appointment_date)}
                                  </span>
                                </div>
                                {appointment.client && (
                                  <>
                                    <div className="appointment-detail">
                                      <span className="appointment-detail-label">
                                        {language === 'ua' ? 'Клієнт' : 'Client'}
                                      </span>
                                      <span className="appointment-detail-value">
                                        {appointment.client.name}
                                      </span>
                                    </div>
                                    <div className="appointment-detail">
                                      <span className="appointment-detail-label">
                                        {language === 'ua' ? 'Телефон' : 'Phone'}
                                      </span>
                                      <a
                                        href={`tel:${appointment.client.phone.replace('+38', '')}`}
                                        className="appointment-detail-value phone"
                                      >
                                        <PhoneIcon className="contact-icon" />
                                        {appointment.client.phone}
                                      </a>
                                    </div>
                                    <div className="appointment-detail">
                                      <span className="appointment-detail-label">
                                        {language === 'ua' ? 'Email' : 'Email'}
                                      </span>
                                      <a
                                        href={`mailto:${appointment.client.email}`}
                                        className="appointment-detail-value email"
                                      >
                                        <EmailIcon className="contact-icon" />
                                        {appointment.client.email}
                                      </a>
                                    </div>
                                  </>
                                )}
                                {appointment.total_price && (
                                  <div className="appointment-detail">
                                    <span className="appointment-detail-label">
                                      {language === 'ua' ? 'Ціна' : 'Price'}
                                    </span>
                                    <span className="appointment-detail-value">
                                      {appointment.total_price} ₴
                                    </span>
                                  </div>
                                )}
                              </div>
                              {appointment.notes && (
                                <div className="appointment-notes">
                                  <div className="appointment-notes-label">
                                    {language === 'ua' ? 'Примітки' : 'Notes'}
                                  </div>
                                  <div className="appointment-notes-value">
                                    {appointment.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default AppointmentsPage;
